<script lang="ts" setup>
import {
  useGraph,
  type ItemChangeEventType,
  type TreeItemsStateType,
  type TreeStateType,
  type TreeViewItemType
} from '#components/treeView';
import TreeViewItem from '#components/treeView/TreeViewItem.vue';
import TreeViewSkeleton from '#components/treeView/TreeViewSkeleton.vue';
import { useVirtualScroll as useVirtualScrollComposable } from '@c4-links/vue3-links-components/virtual-scroll-list';
import type { PropType } from 'vue';
import { computed, ref } from 'vue';

const props = defineProps({
  items: {
    type: Array as PropType<TreeViewItemType[]>,
    default: () => {
      return [];
    }
  },
  parent: {
    type: Object as PropType<TreeViewItemType | undefined>,
    default: undefined
  },
  treeState: {
    type: Object as PropType<TreeStateType>,
    default: null
  },
  expandedIds: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    required: false,
    default: false
  },
  loadingItemsCount: {
    type: Number,
    required: false,
    default: 6
  },
  loadingItemsHasArrow: {
    type: Boolean,
    required: false,
    default: false
  },
  dataTestId: {
    type: String,
    required: false,
    default: undefined
  },
  useVirtualScroll: {
    type: Boolean,
    default: false
  },
  virtualScrollThreshold: {
    type: Number,
    default: 50
  },
  virtualScrollEstimateSize: {
    type: Number,
    default: 48
  },
  virtualScrollOverscan: {
    type: Number,
    default: 10
  },
  virtualScrollMaxHeight: {
    type: String,
    default: '50vh'
  }
});
const emit = defineEmits<{
  onCheckedItemsChange: [state: TreeItemsStateType];
  onCheck: [event: ItemChangeEventType];
}>();
const internalTreeState = computed(
  () =>
    props.treeState ??
    useGraph({
      treeItems: props.items,
      onItemsChecked: (checkedItems) => emit('onCheckedItemsChange', checkedItems),
      onItemChecked: (eventArguments) => emit('onCheck', eventArguments)
    })
);

const expandedKeys = new Set<string>(props.expandedIds);

const scrollContainerRef = ref<HTMLElement | null>(null);

const { shouldUseVirtualScrolling, virtualItems, totalSize, displayItems, hasEverHadItems, measureElement } =
  useVirtualScrollComposable({
    items: computed(() => props.items),
    scrollContainerRef,
    threshold: props.virtualScrollThreshold,
    estimateSize: props.virtualScrollEstimateSize,
    overscan: props.virtualScrollOverscan,
    enabled: computed(() => props.useVirtualScroll && !props.parent),
    isLoading: computed(() => props.isLoading)
  });

const virtualItemsWithData = computed(
  () =>
    virtualItems.value
      .map((virtualItem) => ({
        virtualItem,
        item: displayItems.value[virtualItem.index]
      }))
      .filter((entry) => entry.item !== undefined) as Array<{
      virtualItem: (typeof virtualItems.value)[number];
      item: TreeViewItemType;
    }>
);

const scrollContainerStyle = computed(() => ({
  maxHeight: props.virtualScrollMaxHeight
}));

const virtualListStyle = computed(() => ({
  height: `${totalSize.value}px`
}));

const getVirtualItemStyle = (virtualItem: (typeof virtualItems.value)[number]) => ({
  transform: `translateY(${virtualItem.start}px)`
});
</script>

<template>
  <TreeViewSkeleton
    v-if="isLoading && !hasEverHadItems"
    :loading-items-count="loadingItemsCount"
    :loading-items-has-arrow="loadingItemsHasArrow"
  />
  <div
    v-else-if="shouldUseVirtualScrolling"
    ref="scrollContainerRef"
    class="overflow-y-auto custom-vertical-scrollbar"
    :style="scrollContainerStyle"
  >
    <ul class="relative" :style="virtualListStyle" :data-test-id="dataTestId">
      <li
        v-for="{ virtualItem, item } in virtualItemsWithData"
        :id="item.nodeId"
        :key="Number(virtualItem.key)"
        :ref="measureElement"
        :data-index="virtualItem.index"
        class="absolute top-0 left-0 w-full ml-4"
        :style="getVirtualItemStyle(virtualItem)"
      >
        <TreeViewItem
          v-slot="{ open }"
          class="m-1 p-1"
          :data="item"
          :parent="parent"
          :tree-state="internalTreeState"
          :is-expanded="expandedKeys.has(item.nodeId)"
        >
          <div v-if="open && item.children && item.children.length > 0" class="ml-8 block border-l-transparent">
            <TreeView :items="item.children" :parent="item" :tree-state="internalTreeState" />
          </div>
        </TreeViewItem>
      </li>
    </ul>
  </div>
  <ul v-else-if="displayItems.length > 0 || isLoading" :data-test-id="dataTestId">
    <li v-for="treeViewItem in displayItems" :id="treeViewItem.nodeId" :key="treeViewItem.nodeId" class="ml-4">
      <TreeViewItem
        v-slot="{ open }"
        class="m-1 p-1"
        :data="treeViewItem"
        :parent="parent"
        :tree-state="internalTreeState"
        :is-expanded="expandedKeys.has(treeViewItem.nodeId)"
      >
        <div
          v-if="open && treeViewItem.children && treeViewItem.children.length > 0"
          class="ml-8 block border-l-transparent"
        >
          <TreeView :items="treeViewItem.children" :parent="treeViewItem" :tree-state="internalTreeState" />
        </div>
      </TreeViewItem>
    </li>
  </ul>
</template>
